import { useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { alpha } from '@mui/material/styles';
import { Box, Divider, Typography, MenuItem, IconButton, Button } from '@mui/material';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import DarkMode from '@mui/icons-material/DarkMode';
import LightMode from '@mui/icons-material/LightMode';
import Avatar from '../../components/ui/Avatar';
import { logout } from '../../redux/actions/authActions';
import MenuPopover from '../../components/MenuPopover';
import { useDarkModeContext } from '../../context/DarkModeContext';
import { RED, RED_HOVER } from '../../utils/brandColors';
import { displayHandle } from '../../utils/displayHandle';
import { DOMAIN } from '../../redux/constants';
import { getGuestAvatar } from '../../utils/deviceId';

export default function AccountPopover() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(null);

  const { userInfo } = useSelector((state) => state.userLogin);
  const { mode, toggleMode } = useDarkModeContext();

  const handleOpen = (event) => setOpen(event.currentTarget);
  const handleClose = () => setOpen(null);

  const handleLogout = () => {
    handleClose();
    dispatch(logout());
    navigate('/login');
  };

  if (!userInfo) {
    return (
      <IconButton
        component={RouterLink}
        to="/login"
        aria-label="Log in"
        sx={{ p: 0 }}
      >
        <Avatar src={getGuestAvatar()} sx={{ width: 32, height: 32 }} />
      </IconButton>
    );
  }

  const isAnon = userInfo.isAnon === true;
  const name = displayHandle(userInfo);
  const initial = (name || '?')[0].toUpperCase();
  const avatarSrc = userInfo.avatarUrl || (isAnon ? getGuestAvatar() : undefined);
  // Anon users are NOT signed in. The avatar/popover present as "guest mode"
  // — distinct color, no profile link (anon is excluded from discovery), no
  // log-out (nothing to log out of), and a primary "Sign up" CTA where
  // logout would be for claimed users.
  const headerTo = isAnon ? '/claim' : '/u/me';
  const headerCaption = isAnon ? 'Guest · Sign up to keep your handle' : 'View profile';
  const avatarBg = isAnon ? 'grey.500' : RED;

  return (
    <>
      <IconButton
        ref={anchorRef}
        onClick={handleOpen}
        aria-label={isAnon ? 'Guest options' : 'Account settings'}
        sx={{
          p: 0.25,
          border: open ? `2px solid ${RED}` : '2px solid transparent',
          transition: 'border-color 0.15s ease',
        }}
      >
        <Avatar
          src={avatarSrc}
          name={userInfo.username}
          sx={{
            width: 32, height: 32,
            bgcolor: avatarBg,
            fontSize: '0.85rem',
          }}
        />
      </IconButton>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          p: 0,
          mt: 1.5,
          ml: 0.75,
          width: 220,
          // Don't kiss the viewport edge on narrow phones (#45). The popover is
          // anchored to the avatar's right edge; on a 375px viewport with the
          // 6px ml shift it can otherwise sit flush against the right wall and
          // clip wrapping content. Cap so we always keep ≥8px gutter on each
          // side. maxWidth wins over the fixed `width: 220` when the viewport
          // can't accommodate it.
          maxWidth: 'calc(100vw - 16px)',
          // Replace MUI's default Popover shadow (which bleeds upward as a glow)
          // with a bottom-biased shadow on mobile. Desktop keeps the full soft
          // shadow since the menu floats out in open space.
          boxShadow: { xs: '0 4px 12px rgba(0,0,0,0.08)', sm: '0 5px 5px -3px rgba(0,0,0,0.12), 0 8px 10px 1px rgba(0,0,0,0.08), 0 3px 14px 2px rgba(0,0,0,0.06)' },
          '& .MuiMenuItem-root': {
            typography: 'body2',
            borderRadius: '8px',
            mx: 0.5,
            px: 1.5,
            py: 0.75,
            // MenuItem defaults to white-space: nowrap, which clips long copy
            // (e.g. "Already have an account? Log in") on narrow viewports.
            // Allow wrapping so all bottom-CTA copy stays fully readable.
            whiteSpace: 'normal',
          },
        }}
      >
        {/* Header — avatar + name. For claimed users, the whole row links to
            their profile. For anon (guest) users, the row routes to /claim
            and the caption nudges them to sign up. */}
        <Box
          component={RouterLink}
          to={headerTo}
          onClick={handleClose}
          sx={{
            px: 2, py: 2, display: 'flex', alignItems: 'center', gap: 1.5,
            textDecoration: 'none', color: 'inherit',
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <Avatar
            src={avatarSrc}
            name={userInfo.username}
            sx={{
              width: 40, height: 40,
              bgcolor: avatarBg,
              fontSize: '1.1rem',
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700} noWrap>
              {name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              {headerCaption}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mx: 1 }} />

        {/* Menu items */}
        <Box sx={{ py: 0.5 }}>
          <MenuItem onClick={toggleMode} sx={{ gap: 1.5 }}>
            {mode === 'dark'
              ? <LightMode sx={{ fontSize: 20, color: 'text.secondary' }} />
              : <DarkMode sx={{ fontSize: 20, color: 'text.secondary' }} />
            }
            {mode === 'dark' ? 'Light mode' : 'Dark mode'}
          </MenuItem>
          <MenuItem
            component={RouterLink}
            to="/saved"
            onClick={handleClose}
            sx={{ gap: 1.5 }}
          >
            <BookmarkBorderIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            Saved
          </MenuItem>
        </Box>

        <Divider sx={{ mx: 1 }} />

        {/* Bottom action: Sign up CTA for anon (no logout — nothing to log
            out of), Log out for claimed users. */}
        {isAnon ? (
          <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              component={RouterLink}
              to="/claim"
              onClick={handleClose}
              fullWidth
              variant="contained"
              sx={{
                fontWeight: 700,
                textTransform: 'none',
                fontSize: '0.85rem',
                py: 0.75,
                bgcolor: RED,
                color: '#fff',
                borderRadius: '8px',
                transition: 'background-color 0.15s ease',
                '&:hover': { bgcolor: RED_HOVER },
              }}
            >
              Sign up
            </Button>
            <Button
              component={RouterLink}
              to="/login"
              onClick={handleClose}
              fullWidth
              variant="text"
              sx={{
                color: 'text.secondary',
                fontSize: '0.8rem',
                fontWeight: 600,
                textTransform: 'none',
                py: 0.5,
                borderRadius: '8px',
                '&:hover': { color: RED, bgcolor: alpha(RED, 0.06) },
              }}
            >
              Log in
            </Button>
          </Box>
        ) : (
          <Box sx={{ py: 0.5 }}>
            <MenuItem
              onClick={handleLogout}
              sx={{
                gap: 1.5,
                color: 'text.secondary',
                '&:hover': { color: RED, bgcolor: alpha(RED, 0.06) },
              }}
            >
              <LogoutIcon sx={{ fontSize: 20 }} />
              Log out
            </MenuItem>
          </Box>
        )}
      </MenuPopover>
    </>
  );
}
